import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type FontSize = 'small' | 'medium' | 'large';

interface TypographyStyle {
  fontSize: string;
  fontWeight: string;
  fontFamily: string;
  lineHeight: string;
  letterSpacing: string;
}

interface Theme {
  colors: {
    primary: string;
    secondary: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    border: string;
    divider: string;
    error: string;
    success: string;
    warning: string;
    info: string;
    primaryText: string;
    card: string;
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    xxl: string;
  };
  borderRadius: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
  typography: {
    h1: TypographyStyle;
    h2: TypographyStyle;
    h3: TypographyStyle;
    body: TypographyStyle;
    caption: TypographyStyle;
    button: TypographyStyle;
  };
  fonts: {
    primary: string;
    secondary: string;
    display: string;
  };
  fontSize: FontSize;
}

interface ThemeContextType {
  theme: Theme;
  isDarkMode: boolean;
  toggleTheme: () => void;
  setTheme: (isDark: boolean) => void;
  setFontSize: (size: FontSize) => void;
  getFontSize: () => FontSize;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Font size scaling factors
const fontSizeScaling = {
  small: 0.85,
  medium: 1.0,
  large: 1.2,
};

// Base typography (medium size)
const baseTypography = {
  h1: {
    fontSize: '29px', // Base increased by 1px for all settings
    fontWeight: '700',
    fontFamily: 'var(--font-display)',
    lineHeight: '1.2',
    letterSpacing: '-0.02em',
  },
  h2: {
    fontSize: '21px',
    fontWeight: '600',
    fontFamily: 'var(--font-display)',
    lineHeight: '1.3',
    letterSpacing: '-0.01em',
  },
  h3: {
    fontSize: '17px',
    fontWeight: '600',
    fontFamily: 'var(--font-display)',
    lineHeight: '1.4',
    letterSpacing: '0em',
  },
  body: {
    fontSize: '13px',
    fontWeight: '400',
    fontFamily: 'var(--font-primary)',
    lineHeight: '1.5',
    letterSpacing: '0em',
  },
  caption: {
    fontSize: '11px',
    fontWeight: '400',
    fontFamily: 'var(--font-secondary)',
    lineHeight: '1.4',
    letterSpacing: '0.01em',
  },
  button: {
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: 'var(--font-primary)',
    lineHeight: '1.4',
    letterSpacing: '0.01em',
  },
};

// Function to scale typography based on font size
const getScaledTypography = (fontSize: FontSize) => {
  const scale = fontSizeScaling[fontSize];
  const scaledTypography: typeof baseTypography = {} as typeof baseTypography;

  Object.keys(baseTypography).forEach((key) => {
    const typographyKey = key as keyof typeof baseTypography;
    const base = baseTypography[typographyKey];
    scaledTypography[typographyKey] = {
      ...base,
      fontSize: `${parseFloat(base.fontSize) * scale}px`,
    };
  });

  return scaledTypography;
};

const lightTheme: Omit<Theme, 'typography' | 'fontSize'> = {
  colors: {
    primary: '#007AFF',
    secondary: '#5856D6',
    background: '#FFFFFF',
    surface: '#F2F2F7',
    text: '#000000',
    textSecondary: '#8E8E93',
    textTertiary: '#C7C7CC',
    border: '#C6C6C8',
    divider: '#E5E5EA',
    error: '#FF3B30',
    success: '#34C759',
    warning: '#FF9500',
    info: '#007AFF',
    primaryText: '#FFFFFF',
    card: '#FFFFFF',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
  },
  fonts: {
    primary: 'var(--font-primary)',
    secondary: 'var(--font-secondary)',
    display: 'var(--font-display)',
  },
};

const darkTheme: Omit<Theme, 'typography' | 'fontSize'> = {
  colors: {
    primary: '#0A84FF',
    secondary: '#5E5CE6',
    background: '#000000',
    surface: '#1C1C1E',
    text: '#FFFFFF',
    textSecondary: '#EBEBF5',
    textTertiary: '#3A3A3C',
    border: '#38383A',
    divider: '#48484A',
    error: '#FF453A',
    success: '#30D158',
    warning: '#FF9F0A',
    info: '#0A84FF',
    primaryText: '#FFFFFF',
    card: '#1C1C1E',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
  },
  fonts: {
    primary: 'var(--font-primary)',
    secondary: 'var(--font-secondary)',
    display: 'var(--font-display)',
  },
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const [fontSize, setFontSizeState] = useState<FontSize>(() => {
    const saved = localStorage.getItem('fontSize');
    return (saved as FontSize) || 'medium';
  });

  // Initialize theme on app load
  useEffect(() => {
    const root = document.documentElement;

    if (isDarkMode) {
      root.setAttribute('data-theme', 'dark');
      root.classList.add('dark');
    } else {
      root.setAttribute('data-theme', 'light');
      root.classList.remove('dark');
    }
  }, []); // Run only once on mount

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const setTheme = (isDark: boolean) => {
    setIsDarkMode(isDark);
  };

  const setFontSize = (size: FontSize) => {
    setFontSizeState(size);
    localStorage.setItem('fontSize', size);
  };

  const getFontSize = () => fontSize;

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');

    // Apply theme to document root
    const root = document.documentElement;

    if (isDarkMode) {
      root.setAttribute('data-theme', 'dark');
      root.classList.add('dark');
    } else {
      root.setAttribute('data-theme', 'light');
      root.classList.remove('dark');
    }
  }, [isDarkMode]);

  const baseTheme = isDarkMode ? darkTheme : lightTheme;
  const typography = getScaledTypography(fontSize);

  const theme: Theme = {
    ...baseTheme,
    typography,
    fontSize,
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      isDarkMode,
      toggleTheme,
      setTheme,
      setFontSize,
      getFontSize
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 